import React, { useState, useRef, useEffect } from 'react';

import { Flex, useOutsideClick } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteAgendaModal } from 'screens/oneOnOneRabbit/InstancePage/common/modals/DeleteAgendaModal';
import { OneOnOneSettingsPageReactQueryKeys } from 'screens/oneOnOneRabbit/SettingsPage/constants/OneOnOneSettingsPageReactQueryKeys';
import { AdminAgenda } from 'screens/oneOnOneRabbit/SettingsPage/interfaces/AdminAgenda';
import { setSaveButtonIsDisabled } from 'screens/oneOnOneRabbit/SettingsPage/slice/OneOnOneEventBasedAgendaSlice';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { z } from 'zod';

import { Ellipsis, Trash } from 'assets/assetsV1/iconComponents';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';
// import { Avatar } from 'uiToolkitV1/Avatar';
import { Menu } from 'uiToolkitV1/Menu';

interface Props {
  isDragging?: boolean;
  isDraggingOver?: boolean;
  uiId: string | null | undefined;
}

export type AgendaProps = z.infer<typeof AdminAgenda>;

const AdminAgendaItem = React.memo(({ isDragging, isDraggingOver, ...agenda }: AgendaProps & Props) => {
  const { agendaText, agendaCondition, created, displayOrder, id, orgId, relationType, uiId } = agenda;

  const loggedInUserId: string = useSelector(selectLoggedInEmployeeId);

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [agendaValue, setAgendaValue] = useState(agendaText);

  const [isAgendaError, setIsAgendaError] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const isRegularAgenda = agendaCondition === 'REGULAR';

  useEffect(() => {
    setAgendaValue(agendaText);
  }, [agendaText]);

  const handleAgendaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value.slice(0, 255);
    setAgendaValue(inputValue);
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  const deleteAgenda = () => {
    // gist of this is to remove the agenda from the list
    // if it is a regular agenda, then we will remove it from the regular agenda list
    // if it is an event based agenda, then we will remove it from the event based agenda list
    // data structure for event based agenda is a bit different, so we will have to handle it differently
    // uiId means that new agenda is just on the client side and not saved in the database yet
    // so we will just remove it from the client side
    if (isRegularAgenda) {
      queryClient.setQueryData<
        Array<
          AgendaProps & {
            uiId: string;
          }
        >
      >(
        [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'REGULAR'],
        // @ts-ignore
        (oldData) => {
          if (!oldData) {
            return [];
          }
          if (uiId) {
            return oldData.filter((agendaItem) => {
              if (agendaItem?.uiId && agendaItem?.uiId === uiId) {
                return false;
              }
              return true;
            });
          }
          return oldData.map((agendaItem) => {
            if (agendaItem?.id === id) {
              return {
                ...agendaItem,
                isDeleted: true,
              };
            }
            return agendaItem;
          });
        }
      );
    } else {
      queryClient.setQueryData<
        Array<
          AgendaProps & {
            uiId: string;
          }
        >
      >([OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'], (oldData) => {
        if (uiId) {
          return {
            ...oldData,
            [agendaCondition]: [
              ...oldData[agendaCondition].filter((agendaItem) => {
                if (agendaItem?.uiId && agendaItem?.uiId === uiId) {
                  return false;
                }
                return true;
              }),
            ],
          };
        }
        return {
          ...oldData,
          [agendaCondition]: [
            ...oldData[agendaCondition].map((agendaItem) => {
              if (agendaItem?.id === id) {
                return {
                  ...agendaItem,
                  isDeleted: true,
                };
              }
              return agendaItem;
            }),
          ],
        };
      });
    }
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  const menuOptions = [
    {
      label: 'Delete Agenda',
      onClick: () => setIsDeleteModalOpen(true),
      Icon: Trash,
    },
  ];

  const updateAgendaHandler = async ({ agendaText }: { agendaText: string; isCompleted?: boolean }) => {
    if (agendaText === agenda.agendaText) {
      return;
    }
    if (!agendaText || agendaText.trim() === '') {
      setIsAgendaError(true);
      inputRef.current?.focus();
      return;
    }
    try {
      const updatedAgendaText = !agendaText || agendaText.trim() === '' ? agenda.agendaText : agendaText;
      const updatedAgenda = {
        ...agenda,
        agendaText: updatedAgendaText,
      };

      if (isRegularAgenda) {
        queryClient.setQueryData<
          Array<
            AgendaProps & {
              uiId: string;
            }
          >
        >([OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'REGULAR'], (oldData) => {
          if (!oldData) {
            return [];
          }
          return oldData.map((agendaItem) => {
            if (agendaItem?.uiId && agendaItem?.uiId === uiId) {
              return updatedAgenda;
            }
            if (agendaItem?.id && agendaItem?.id === id) {
              return updatedAgenda;
            }
            return agendaItem;
          });
        });
      } else {
        queryClient.setQueryData<
          Array<
            AgendaProps & {
              uiId: string;
            }
          >
        >([OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'], (oldData) => ({
          ...oldData,
          [agendaCondition]: [
            ...oldData[agendaCondition].map((agendaItem) => {
              if (agendaItem?.uiId && agendaItem?.uiId === uiId) {
                return updatedAgenda;
              }

              if (agendaItem?.id && agendaItem?.id === id) {
                return updatedAgenda;
              }
              return agendaItem;
            }),
          ],
        }));
      }
    } catch (e) {
      console.error('Error updating agenda : ', e);
    } finally {
      setIsAgendaError(false);
    }
  };

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      updateAgendaHandler({ agendaText: agendaValue });
      if (!agendaValue || agendaValue.trim() === '') {
        inputRef.current?.focus();
      } else {
        inputRef.current?.blur();
      }
    }
  };

  useOutsideClick({
    ref: inputRef,
    handler: () => updateAgendaHandler({ agendaText: agendaValue }),
  });

  return (
    <>
      <Flex
        sx={{
          pointerEvents: isDraggingOver ? 'none' : 'auto',
          ':hover': {
            backgroundColor: isDragging ? '#F6FAFE' : 'white',
          },
          backgroundColor: isDragging ? '#F6FAFE' : 'transparent',
          '&:hover .admin-reorder-handle': {
            visibility: 'visible',
          },
          '&:hover .admin-agenda-item-input': {
            borderBottom: '1px solid #333333',
            borderBottomStyle: 'solid',
          },
        }}
        p="18px 24px"
        alignItems="center"
        w="100%"
        minHeight="56px"
        flexDir="column"
        position="static"
      >
        <Flex alignItems="flex-start" w="100%">
          <Flex flexDir="column" w="100%">
            <Flex alignItems="center" w="100%">
              <AutoResizeInput
                ref={inputRef}
                isDashed
                value={agendaValue}
                onChange={handleAgendaChange}
                onKeyDown={onEnterPress}
                isInvalid={isAgendaError}
                sx={{
                  fontWeight: '600',
                }}
                className="admin-agenda-item-input"
              />
              <Menu
                sx={{ background: 'transparent !important', marginRight: '-28px' }}
                menuList={menuOptions}
                MenuIcon={Ellipsis}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <DeleteAgendaModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSave={() => {
          deleteAgenda();
          setIsDeleteModalOpen(false);
        }}
        isDeletingFromSettingsPage
      />
    </>
  );
});

export { AdminAgendaItem };
